import { extendTailwindMerge } from 'tailwind-merge';

/**
 * Anything that can evaluate to a class string or a falsy value. Matches
 * the shape produced by `condition && 'some-class'` expressions — including
 * the less-common falsy types like `0n` that TS widens ReactNode-ish
 * expressions to.
 */
type ClassValue = string | number | bigint | false | null | undefined;

/**
 * tailwind-merge configured with our role-named radius/shadow tokens so they
 * dedupe against Tailwind's default `rounded-*` / `shadow-*` scale. Without
 * this, `cn('shadow-md', 'shadow-elevated')` would keep both classes.
 */
const twMerge = extendTailwindMerge({
  extend: {
    theme: {
      radius: ['control', 'surface', 'pill'],
      shadow: ['card', 'elevated', 'popover', 'dialog'],
    },
  },
});

/**
 * Compose Tailwind class strings with conflict resolution.
 *
 * Accepts any number of class fragments (including falsy values, which are
 * skipped) and returns a deduplicated string where later utilities win over
 * earlier ones on the same property. This is the canonical way to merge
 * `baseStyles + variantStyles + className` inside DS primitives so that a
 * caller's `className="shadow-card"` override actually wins against a
 * variant's `shadow-elevated`.
 *
 * @example
 *   cn('px-4 py-3 rounded-surface', error && 'border-error', className)
 */
export function cn(...classes: ClassValue[]): string {
  return twMerge(classes.filter(Boolean).join(' '));
}
